import React, {useState} from 'react';
import {Box, Button, CircularProgress, Typography} from '@mui/material';
import {AutoFixHigh} from '@mui/icons-material';
import {getErrorMessage} from '../utilities/Utils';

// Super-admin action: re-seed the SRD spell list (POST /spells/srd/import) from dnd5eapi.co,
// which also fills in saves and level scaling on existing 2014-rules characters' spells.
// Safe to repeat - it updates the list in place and only fills empty character-spell fields.
export default function SrdSpellImport() {
    const [state, setState] = useState({status: 'idle'});

    const run = async () => {
        setState({status: 'running'});
        try {
            const user = JSON.parse(sessionStorage.getItem('user'));
            const res = await fetch(`${process.env.REACT_APP_API_PROXY}/spells/srd/import`, {
                method: 'POST',
                headers: {'Authorization': 'Bearer ' + user.accessToken},
            });
            if (!res.ok) {
                // A proxy in front of the server can give up on a request this long while the
                // import itself carries on; say so rather than reporting a failure.
                if (res.status === 502 || res.status === 504) {
                    setState({status: 'error', message: 'The request timed out at the proxy, but the import probably carried on - check the server log for "Imported N SRD spells".'});
                    return;
                }
                throw new Error(res.status === 403
                    ? 'Refused: this needs the super admin role (log out and back in after it is granted).'
                    : await getErrorMessage(res, `Import failed (${res.status})`));
            }
            const {imported, backfilled} = await res.json();
            setState({status: 'done', imported, backfilled});
        } catch (err) {
            setState({status: 'error', message: err.message || 'Import failed'});
        }
    };

    const running = state.status === 'running';
    return (
        <Box>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap'}}>
                <Typography sx={{fontSize: '0.75rem', color: 'var(--lm-tx-888888)', flex: '1 1 220px'}}>
                    Re-import the SRD spell list from dnd5eapi.co and fill in saves and level scaling on existing
                    characters' spells. Takes a minute or two; safe to run again.
                </Typography>
                <Button variant="outlined" size="small" onClick={run} disabled={running}
                    startIcon={running ? <CircularProgress size={14} sx={{color: 'var(--lm-tx-th-c9a0f0)'}}/> : <AutoFixHigh/>}
                    sx={{
                        color: 'var(--lm-tx-th-c9a0f0)', borderColor: 'rgba(var(--th-6c4f9b-rgb), 0.8)', textTransform: 'none',
                        '&:hover': {borderColor: 'var(--lm-bd-th-c9a0f0)', backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.15)'},
                    }}>
                    {running ? 'Importing…' : 'Re-import SRD spells'}
                </Button>
            </Box>
            {state.status === 'done' && (
                <Typography role="status" sx={{fontSize: '0.75rem', color: 'var(--lm-tx-66bb6a)', mt: 1}}>
                    Imported {state.imported} spells; filled in {state.backfilled} character spell{state.backfilled === 1 ? '' : 's'}.
                </Typography>
            )}
            {state.status === 'error' && (
                <Typography role="alert" sx={{fontSize: '0.75rem', color: 'var(--lm-tx-e53935)', mt: 1}}>{state.message}</Typography>
            )}
        </Box>
    );
}
